import React, { useState, useEffect } from 'react';
import { RefreshCw, Plus, Pencil, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { calendarSourcesApi } from '../api/services';
import type { CalendarSource } from '../types/models';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { cn } from '@/lib/utils';

export default function CalendarSourcesCard() {
  const confirm = useConfirm();
  const [sources, setSources] = useState<CalendarSource[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<Partial<CalendarSource> | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  const loadSources = async () => {
    try {
      const all = await calendarSourcesApi.getAll();
      // ICS only here; OAuth sources live in ConnectCalendarCard.
      setSources(all.filter(s => !s.sourceType || s.sourceType === 'ICS'));
    } catch (err) {
      console.error('Failed to load calendar sources', err);
    }
  };

  useEffect(() => { loadSources(); }, []);

  const handleSave = async () => {
    if (!editItem?.name || !editItem?.icsUrl) return;
    try {
      if (editItem.id) {
        await calendarSourcesApi.update(editItem.id, editItem);
      } else {
        await calendarSourcesApi.create(editItem);
      }
      setDialogOpen(false);
      setEditItem(null);
      loadSources();
    } catch (err) {
      console.error('Failed to save calendar source', err);
    }
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: 'Remove this calendar source?',
      body: 'Events synced from this source will disappear from your calendar.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      await calendarSourcesApi.delete(id);
      loadSources();
    } catch (err) {
      console.error('Failed to delete', err);
    }
  };

  const handleSync = async (id: string) => {
    setSyncingId(id);
    try {
      await calendarSourcesApi.sync(id);
      loadSources();
    } catch (err) {
      console.error('Failed to sync', err);
    } finally {
      setSyncingId(null);
    }
  };

  return (
    <Card className="mb-4">
      <CardContent className="pt-6">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            ICS Calendar Sources
          </p>
          <Button
            size="sm"
            variant="ghost"
            className="gap-1.5"
            onClick={() => {
              setEditItem({ name: '', icsUrl: '', isActive: true });
              setDialogOpen(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Add ICS Source
          </Button>
        </div>
        {sources.length === 0 ? (
          <p className="text-sm text-muted-foreground">No ICS calendar sources configured</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-2 pr-3">Name</th>
                  <th className="py-2 pr-3">ICS URL</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Last Synced</th>
                  <th className="py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sources.map((source) => (
                  <tr key={source.id} className="border-b border-border/60 hover:bg-muted/50">
                    <td className="py-2 pr-3 font-medium">{source.name}</td>
                    <td className="py-2 pr-3">
                      <span className="font-mono text-xs text-muted-foreground">
                        {source.icsUrl.length > 50 ? source.icsUrl.substring(0, 50) + '...' : source.icsUrl}
                      </span>
                    </td>
                    <td className="py-2 pr-3">
                      <Badge
                        variant="outline"
                        className={cn(
                          'h-[22px]',
                          source.isActive
                            ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                            : 'border-border text-muted-foreground',
                        )}
                      >
                        {source.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="py-2 pr-3 text-xs text-muted-foreground">
                      {source.lastSyncedAt
                        ? format(new Date(source.lastSyncedAt), 'dd MMM HH:mm')
                        : 'Never'}
                    </td>
                    <td className="py-2 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleSync(source.id)}
                        disabled={syncingId === source.id}
                        title="Sync"
                      >
                        <RefreshCw className={cn('h-4 w-4', syncingId === source.id && 'animate-spin')} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => { setEditItem(source); setDialogOpen(true); }}
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() => handleDelete(source.id)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editItem?.id ? 'Edit Calendar Source' : 'Add Calendar Source'}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="cs-name">Name</Label>
                <Input
                  id="cs-name"
                  value={editItem?.name || ''}
                  onChange={e => setEditItem(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g. Work Calendar"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="cs-url">ICS URL</Label>
                <Input
                  id="cs-url"
                  value={editItem?.icsUrl || ''}
                  onChange={e => setEditItem(prev => ({ ...prev, icsUrl: e.target.value }))}
                  placeholder="https://example.com/calendar.ics"
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch
                  checked={editItem?.isActive ?? true}
                  onCheckedChange={(v) => setEditItem(prev => ({ ...prev, isActive: v }))}
                />
                <span>Active</span>
              </label>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
